import { IMAGES, COMPANY, STATS } from "@/lib/site";
import { Section, Overline, FadeIn, PageHero } from "@/components/Primitives";
import { Sprout, Leaf, Globe, Compass } from "lucide-react";

const values = [
  {
    icon: Sprout,
    title: "Integrity at Origin",
    body: "We commit only to what we can deliver. Specifications are honoured from contract to containers.",
  },
  {
    icon: Leaf,
    title: "Sustainable Sourcing",
    body: "We work with farmer co-operatives and reputable processors who share our standards.",
  },
  {
    icon: Globe,
    title: "Execution Excellence",
    body: "A UAE HQ with origin desks — disciplined execution across time zones and trade lanes.",
  },
  {
    icon: Compass,
    title: "Long-Term Partnerships",
    body: "We measure success in decades, not deals — building relationships that compound.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        overline="About the FARMICO Group"
        title={
          <>
            UAE Roots,
            <br />
            <em className="not-italic text-[#2A4D3B]">Global Appetite.</em>
          </>
        }
        subtitle="Two specialised entities — Farmico International Trading FZE and Farmico Proteins FZCO — operating under one trading philosophy: source the right crop, from the right origin, for the right buyer."
        image={IMAGES.harvest}
      />

      <Section testid="about-story" className="bg-[#FDFBF7]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Overline>Our story</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
              From a field walk to a global trading floor.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#4A4540] leading-relaxed text-base sm:text-lg">
            <p>
              The Farmico Group was built around a simple belief — that great trading houses are
              measured by what they deliver, not what they promise. We started by spending time at
              origin: in Pigeon Peas farms across Africa, Lentil belts in Canada, Beans markets in
              Brazil and pulse yards across the subcontinent.
            </p>
            <p>
              That field-level discipline is now the foundation of our UAE trading desk. Today, the
              {" "}{COMPANY.fze}{" "}executes physical trades across pulses, oilseeds and select cash
              crops, while {" "}{COMPANY.fzco}{" "}operates as a specialist international brokerage
              for the global pulses industry.
            </p>
            <p className="font-bold text-[#1A1A1A]">
              We trade volume — but we trade trust first.
            </p>
          </div>
        </div>
      </Section>

      <section className="bg-[#F6F4F0] py-24 px-6 sm:px-12 lg:px-24" data-testid="about-stats">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.08}>
              <p className="font-serif-display text-5xl sm:text-6xl text-[#2A4D3B]">{s.value}</p>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#4A4540] leading-snug">
                {s.label}
              </p>
            </FadeIn>
          ))}
        </div>
      </section>

      <Section testid="about-values" className="bg-[#FDFBF7]">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <Overline>Our values</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.06] text-[#1A1A1A]">
              What we won't compromise on.
            </h2>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {values.map((v, i) => (
              <FadeIn key={v.title} delay={i * 0.08}>
                <article className="h-full rounded-3xl bg-white border border-[#EAE7E0] p-8 hover:-translate-y-1 hover:shadow-xl transition-all">
                  <v.icon className="h-7 w-7 text-[#2A4D3B]" />
                  <h3 className="mt-6 font-serif-display text-xl text-[#1A1A1A]">{v.title}</h3>
                  <p className="mt-3 text-sm text-[#4A4540] leading-relaxed">{v.body}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </Section>

      <Section testid="about-entities" className="bg-[#1F3A2D] text-[#F6F4F0]">
        <div className="grid lg:grid-cols-2 gap-12">
          <FadeIn>
            <div className="h-full p-10 rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
              <h3 className="font-serif-display text-3xl">{COMPANY.fze}</h3>
              <p className="mt-5 text-sm text-[#C8C2B5] leading-relaxed">
                Multi-commodity physical trading desk for pulses, oilseeds and select cash crops.
                Registered as a Free Zone Establishment in Dubai, operating across containerised and
                break-bulk shipments globally.
              </p>
              <ul className="mt-8 space-y-2 text-sm text-[#F6F4F0]/90">
                <li>· Physical sourcing and supply</li>
                <li>· Multi-origin coverage</li>
                <li>· End-to-end logistics & documentation</li>
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.12}>
            <div className="h-full p-10 rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
              <h3 className="font-serif-display text-3xl">{COMPANY.fzco}</h3>
              <p className="mt-5 text-sm text-[#C8C2B5] leading-relaxed">
                International brokerage house specialised in the global pulses industry. Connecting
                origin shippers and destination importers with daily market intelligence and trusted
                execution.
              </p>
              <ul className="mt-8 space-y-2 text-sm text-[#F6F4F0]/90">
                <li>· Origin-to-destination matching</li>
                <li>· Pulses market intelligence</li>
                <li>· Independent, transparent brokerage</li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
